import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const CarbonOffset = () => {
  // State variables for Carbon Offset calculation
  const [emissions, setEmissions] = useState(0); // Emissions (in kg or metric tons)
  const [offsetFactor, setOffsetFactor] = useState(0); // Offset Factor based on activity type
  const [calculatedOffset, setCalculatedOffset] = useState(0); // Calculated carbon offset
  const [unit, setUnit] = useState("kg"); // User-selected unit for emissions (kg or tons)

  // Event handlers to update state values
  const handleEmissionsChange = (e) => setEmissions(parseFloat(e.target.value) || 0);
  const handleOffsetFactorChange = (e) => setOffsetFactor(parseFloat(e.target.value) || 0);

  const handleUnitChange = (e) => setUnit(e.target.value); // Handle unit change (kg or tons)

  // useEffect hook to calculate the Carbon Offset
  useEffect(() => {
    // Carbon Offset = Emissions x Offset Factor
    if (emissions > 0 && offsetFactor > 0) {
      let carbonOffset = emissions * offsetFactor;

      // Convert the result to metric tons if the unit is 'tons'
      if (unit === "tons") {
        carbonOffset = carbonOffset / 1000; // Convert kg to tons (1 ton = 1000 kg)
      }

      setCalculatedOffset(carbonOffset);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [emissions, offsetFactor, unit]);

  return (
    <Layout title="Carbon Offset Calculator" path="carbon-offset">
      <section className="section-padding" id="carbon-offset-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Carbon Offset Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Carbon Offset Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Emissions */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="emissions" className="form-label">
                        Emissions
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Emissions:</strong> The total amount of greenhouse gas emissions you want to offset, usually in kilograms or metric tons."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="emissions"
                        placeholder="Enter emissions"
                        value={emissions || ""}
                        onChange={handleEmissionsChange}
                      />
                    </div>

                    {/* Input for Offset Factor */}
                    <div className="col-md-7 col-lg-5">
                      <label htmlFor="offsetFactor" className="form-label">
                        Offset Factor (based on activity type)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Offset Factor:</strong> A factor that represents the amount of CO2 offset per unit of activity or emissions. This varies based on the type of activity you're offsetting."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="offsetFactor"
                        placeholder="Enter offset factor"
                        value={offsetFactor || ""}
                        onChange={handleOffsetFactorChange}
                      />
                    </div>

                    {/* Unit Selection (kg or tons) */}
                    <div className="col-md-5 col-lg-3">
                      <label htmlFor="unit" className="form-label">
                        Select Unit
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Select Unit:</strong> Choose the unit of measurement for your emissions and offset (kilograms or metric tons)."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <select
                        className="form-select"
                        id="unit"
                        value={unit}
                        onChange={handleUnitChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="tons">Metric Tons (tons)</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Calculated Carbon Offset:{" "}
                        <span className="fw-bold ms-3">
                          {calculatedOffset === 0
                            ? "0"
                            : calculatedOffset.toFixed(2)}{" "}
                          {unit === "kg" ? "kg" : "tons"}
                        </span>
                      </h6>
                    </div>
                    {/* Showing the result in both units (kg and tons) */}
                    {unit === "kg" && (
                      <div className="mt-2">
                        <h6>
                          In Metric Tons:{" "}
                          <span className="fw-bold">
                            {calculatedOffset === 0
                              ? "0"
                              : (calculatedOffset / 1000).toFixed(3)}{" "}
                            tons
                          </span>
                        </h6>
                      </div>
                    )}
                    {unit === "tons" && (
                      <div className="mt-2">
                        <h6>
                          In Kilograms:{" "}
                          <span className="fw-bold">
                            {calculatedOffset === 0
                              ? "0"
                              : (calculatedOffset * 1000).toFixed(2)}{" "}
                            kg
                          </span>
                        </h6>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Carbon Offset Calculator
                </h3>

                {/* What is a Carbon Offset Calculator? */}
                <h4 className="mt-3 mb-3">What is a Carbon Offset Calculator?</h4>
                <p>
                  The **Carbon Offset Calculator** helps organizations and individuals calculate how much carbon offset they need to purchase or invest in to balance out their carbon emissions. This is vital for reducing environmental impact and achieving sustainability goals.
                </p>
                <ul>
                  <li><strong>Emissions:</strong> The amount of CO2 or greenhouse gases emitted due to activities like transportation, manufacturing, or energy consumption.</li>
                  <li><strong>Offset Factor:</strong> A multiplier based on the type of activity or initiative used to calculate the required offset (e.g., tree planting, renewable energy projects).</li>
                </ul>

                {/* Why Use a Carbon Offset Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Carbon Offset Calculator?</h4>
                <p>
                  This tool is essential for understanding how much carbon offset is necessary to neutralize emissions from activities. It allows businesses, NGOs, and individuals to calculate their contribution to carbon reduction and make informed decisions to reduce their environmental footprint.
                </p>

                {/* Benefits of Using a Carbon Offset Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Carbon Offset Calculator</h4>
                <ul>
                  <li><strong>Supports Sustainability Initiatives:</strong> Helps organizations and individuals take meaningful steps toward reducing their carbon impact.</li>
                  <li><strong>Helps Businesses Meet Carbon Reduction Targets:</strong> Assists corporations in setting and achieving carbon neutrality or sustainability goals.</li>
                  <li><strong>Provides Insight for Eco-Conscious Consumers:</strong> Enables consumers to make informed choices about reducing their carbon footprint.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Carbon Offset Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Corporations:</strong> Helps businesses calculate their carbon emissions and decide how much to offset in order to meet sustainability targets.</li>
                  <li><strong>Environmental NGOs:</strong> Assists non-profits in managing and tracking emissions to support environmental advocacy.</li>
                  <li><strong>Sustainability Projects:</strong> Supports eco-friendly initiatives in calculating the necessary carbon offset to maintain project goals.</li>
                </ul>

                {/* Question 1 */}
                <h4>How do I calculate my carbon emissions?</h4>
                <p>Carbon emissions can be calculated based on the energy consumed, the distance traveled, or the manufacturing activities involved. Each activity has a specific emission factor.</p>

                {/* Question 2 */}
                <h4>What is an offset factor?</h4>
                <p>An offset factor is a value that represents the amount of carbon dioxide equivalent that can be reduced or offset by a specific type of activity, such as renewable energy, carbon credits, or reforestation efforts.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CarbonOffset;
